import React, { useState, useEffect } from 'react';
import { useCPRLog } from './CPRLog';
import { loadCurrentState, saveCurrentState } from './CprState/storage';
import { cprEventEmitter, EVENTS } from './cprEvents';
import './VitalSigns.scss';
import { useTranslation } from 'react-i18next';

interface VitalSigns {
  timestamp: string;
  heartRate: string;
  systolicBP: string;
  diastolicBP: string;
  saturation: string;
  etco2: string;
  glucose: string;
  temperature: string;
  consciousness: string;
}

interface ValidationErrors {
  heartRate?: string;
  systolicBP?: string;
  diastolicBP?: string;
  saturation?: string;
  temperature?: string;
}

const initialVitalSigns: VitalSigns = {
  timestamp: '',
  heartRate: '',
  systolicBP: '',
  diastolicBP: '',
  saturation: '',
  etco2: '',
  glucose: '',
  temperature: '',
  consciousness: '',
};

const consciousnessLevels = ['Alert', 'Verbal', 'Pain', 'Unresponsive'];

const VitalSigns: React.FC = () => {
  const { addEntry } = useCPRLog();
  const { t } = useTranslation(['resus']);
  const [vitalSigns, setVitalSigns] = useState<VitalSigns[]>(() => {
    const savedState = loadCurrentState();
    return savedState?.vitalSigns?.signs || [];
  });
  
  const [currentVitalSigns, setCurrentVitalSigns] = useState<VitalSigns>(initialVitalSigns);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      setVitalSigns([]);
      setCurrentVitalSigns(initialVitalSigns);
      setValidationErrors({});
    });

    return () => unsubscribe();
  }, []);

  const isValidNumber = (value: string): boolean => {
    // Allow empty string
    if (!value) return true;
    
    // Allow only digits and at most one decimal point
    // This regex matches:
    // - Optional digits before decimal point
    // - Optional decimal point followed by at most one digit
    return /^\d*\.?\d{0,1}$/.test(value);
  };

  const validateField = (name: string, value: string): string | undefined => {
    if (!value) return undefined;

    if (!isValidNumber(value)) {
      return t('validation.enterValidNumber');
    }

    const numValue = parseFloat(value);
    
    switch (name) {
      case 'temperature':
        if (numValue < 0 || numValue > 50) return t('validation.enterValueInRange', { min: 0, max: 50 });
        break;
      case 'heartRate':
        if (numValue < 0 || numValue > 220) return t('validation.enterValueInRange', { min: 0, max: 220 });
        break;
      case 'saturation':
        if (numValue < 0 || numValue > 100) return t('validation.enterValueInRange', { min: 0, max: 100 });
        break;
      case 'systolicBP':
      case 'diastolicBP':
        if (numValue < 0 || numValue > 200) return t('validation.enterValueInRange', { min: 0, max: 200 });
        break;
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCurrentVitalSigns(prev => ({ ...prev, [name]: value }));
    
    if (name !== 'consciousness') {
      const error = validateField(name, value);
      setValidationErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();

    const hasAnyValue = Object.values(currentVitalSigns).some(value => 
      value !== '' && value !== initialVitalSigns.timestamp
    );

    if (!hasAnyValue) {
      return; // Don't submit if all fields are empty
    }

    const errors: ValidationErrors = {};
    if (currentVitalSigns.temperature) {
      errors.temperature = validateField('temperature', currentVitalSigns.temperature);
    }
    if (currentVitalSigns.heartRate) {
      errors.heartRate = validateField('heartRate', currentVitalSigns.heartRate);
    }
    if (currentVitalSigns.saturation) {
      errors.saturation = validateField('saturation', currentVitalSigns.saturation);
    }
    if (currentVitalSigns.systolicBP) {
      errors.systolicBP = validateField('systolicBP', currentVitalSigns.systolicBP);
    }
    if (currentVitalSigns.diastolicBP) {
      errors.diastolicBP = validateField('diastolicBP', currentVitalSigns.diastolicBP);
    }
    setValidationErrors(errors);

    const hasErrors = Object.values(errors).some(error => error !== undefined);
    if (hasErrors) {
      return; // Don't submit if there are validation errors
    }

    const newVitalSigns = {
      ...currentVitalSigns,
      timestamp: new Date().toISOString(),
    };
    
    const updatedSigns = [...vitalSigns, newVitalSigns];
    setVitalSigns(updatedSigns);
    
    saveCurrentState({
      vitalSigns: {
        signs: updatedSigns
      }
    }, 'vitalSigns');

    const logEntries: string[] = [];
    if (newVitalSigns.heartRate) logEntries.push(t('vitalSigns.heartRateEntry', { value: newVitalSigns.heartRate }));
    if (newVitalSigns.saturation) logEntries.push(t('vitalSigns.saturationEntry', { value: newVitalSigns.saturation }));
    if (newVitalSigns.systolicBP) logEntries.push(t('vitalSigns.systolicEntry', { value: newVitalSigns.systolicBP }));
    if (newVitalSigns.diastolicBP) logEntries.push(t('vitalSigns.diastolicEntry', { value: newVitalSigns.diastolicBP }));    
    if (newVitalSigns.etco2) logEntries.push(t('vitalSigns.etco2Entry', { value: newVitalSigns.etco2 }));
    if (newVitalSigns.glucose) logEntries.push(t('vitalSigns.glucoseEntry', { value: newVitalSigns.glucose }));
    if (newVitalSigns.temperature) logEntries.push(t('vitalSigns.temperatureEntry', { value: newVitalSigns.temperature }));
    if (newVitalSigns.consciousness) logEntries.push(t('vitalSigns.consciousnessEntry', { value: newVitalSigns.consciousness }));

    addEntry({
      timestamp: newVitalSigns.timestamp,
      text: t('vitalSigns.measurementLog', { entries: logEntries.join(' , ') }),
      type: 'measurement',
      isImportant: false,
    });

    setCurrentVitalSigns(initialVitalSigns);
    setValidationErrors({});
  };
  
  const handleReset = () => {
    setCurrentVitalSigns(initialVitalSigns);
    setValidationErrors({});
  };

  const renderVitalSignsHistory = (signs: VitalSigns) => {
    const entries = [
      { label: t('vitalSigns.heartRate'), value: signs.heartRate, unit: t('vitalSigns.bpmUnit') },
      { label: t('vitalSigns.saturation'), value: signs.saturation, unit: '%' },
      { label: t('vitalSigns.systolicBP'), value: signs.systolicBP, unit: '' },
      { label: t('vitalSigns.diastolicBP'), value: signs.diastolicBP, unit: '' },      
      { label: t('vitalSigns.etco2'), value: signs.etco2, unit: '' },
      { label: t('vitalSigns.glucose'), value: signs.glucose, unit: t('vitalSigns.glucoseUnit') },
      { label: t('vitalSigns.temperature'), value: signs.temperature, unit: t('vitalSigns.temperatureUnit') },
      { label: t('vitalSigns.consciousness'), value: signs.consciousness, unit: '' },
    ].filter(entry => entry.value);
  
    return (
      <div className="vital-signs-entry" key={signs.timestamp}>
        <p className="timestamp"><strong>{new Date(signs.timestamp).toLocaleTimeString('he-IL')}</strong></p>
        <div className="vital-signs-grid">
          {entries.map((entry) => (
            <p key={`${signs.timestamp}-${entry.label}`}>{entry.label}: {entry.value} {entry.unit}</p>
          ))}
        </div>
      </div>
    );
  };
  

  return (
    <div className="vital-signs">
      <form style={{maxWidth: "90%"}}>
  <div className="input-group">
    <label htmlFor="heartRate">{t('vitalSigns.heartRate')} ({t('vitalSigns.bpmUnit')}):</label>
    <div className="input-with-validation">
      <input
        type="text"
        inputMode='decimal'
        id="heartRate"
        name="heartRate"
        value={currentVitalSigns.heartRate}
        onFocus={(e) => e.target.select()}
        onChange={handleInputChange}
      />
      {validationErrors.heartRate && (
        <div className="validation-error">{validationErrors.heartRate}</div>
      )}
    </div>
  </div>
  
  <div className="input-group">
    <label htmlFor="etco2">{t('vitalSigns.etco2')}:</label>
    <input
      type="text"
      inputMode='decimal'
      id="etco2"
      name="etco2"
      onFocus={(e) => e.target.select()}
      value={currentVitalSigns.etco2}
      onChange={handleInputChange}
    />
  </div>

  <div className="input-group">
    <label htmlFor="systolicBP">{t('vitalSigns.systolicBP')}:</label>
    <div className="input-with-validation">
      <input
        type="text"
        inputMode='decimal'
        id="systolicBP"
        name="systolicBP"
        onFocus={(e) => e.target.select()}
        value={currentVitalSigns.systolicBP}
        onChange={handleInputChange}
      />
      {validationErrors.systolicBP && (
        <div className="validation-error">{validationErrors.systolicBP}</div>
      )}
    </div>
  </div>

  <div className="input-group">
    <label htmlFor="diastolicBP">{t('vitalSigns.diastolicBP')}:</label>
    <div className="input-with-validation">
      <input
        type="text"
        inputMode='decimal'
        id="diastolicBP"
        name="diastolicBP"
        value={currentVitalSigns.diastolicBP}
        onFocus={(e) => e.target.select()}
        onChange={handleInputChange}
      />
      {validationErrors.diastolicBP && (
        <div className="validation-error">{validationErrors.diastolicBP}</div>
      )}
    </div>
  </div>

  <div className="input-group">
    <label htmlFor="glucose">{t('vitalSigns.glucose')} ({t('vitalSigns.glucoseUnit')}):</label>
    <input
      type="text"
      inputMode='decimal'
      id="glucose"
      name="glucose"
      value={currentVitalSigns.glucose}
      onFocus={(e) => e.target.select()}
      onChange={handleInputChange}
    />
  </div>

  <div className="input-group">
    <label htmlFor="temperature">{t('vitalSigns.temperature')} (°C):</label>
    <div className="input-with-validation">
      <input
        type="text"
        inputMode='decimal'
        id="temperature"
        name="temperature"
        value={currentVitalSigns.temperature}
        onFocus={(e) => e.target.select()}
        onChange={handleInputChange}
      />
      {validationErrors.temperature && (
        <div className="validation-error">{validationErrors.temperature}</div>
      )}
    </div>
  </div>

  <div className="input-group">
    <label htmlFor="saturation">{t('vitalSigns.saturation')} (%):</label>
    <div className="input-with-validation">
      <input
        type="text"
        inputMode='decimal'
        id="saturation"
        name="saturation"
        value={currentVitalSigns.saturation}
        onFocus={(e) => e.target.select()}
        onChange={handleInputChange}
      />
      {validationErrors.saturation && (
        <div className="validation-error">{validationErrors.saturation}</div>
      )}
    </div>
  </div>

  <div className="input-group">
    <label htmlFor="consciousness">{t('vitalSigns.consciousness')}:</label>
    <select
      id="consciousness"
      name="consciousness"
      value={currentVitalSigns.consciousness}
      onChange={handleInputChange}
    >
      <option value="">{t('vitalSigns.selectConsciousness')}</option>
      {consciousnessLevels.map(level => (
        <option key={level} value={level}>{level}</option>
      ))}
    </select>
  </div>


</form>
<div className="row form-group">
    <div className="col text-center">
      <button
        type="button"
        className="btn btn-update"
        style={{ marginLeft: "15px" }}
        data-testid="vital-signs-update"
        onClick={handleUpdate}
      >
        {t('buttons.update')}
      </button>
      <button
        type="button"
        className="btn btn-reset"
        data-testid="vital-signs-clear"
        onClick={handleReset}
      >
        {t('buttons.clear')}
      </button>
    </div>
  </div>
      {vitalSigns.length > 0 && <div className="vital-signs-history">
        <h4>{t('vitalSigns.history')}</h4>
        {[...vitalSigns].reverse().map((signs) => renderVitalSignsHistory(signs))}
      </div>}
    </div>
  );
};

export default VitalSigns;